import React, { useState } from "react";
import {
  Card,
  CardContent,
  IconButton,
  Typography,
  Button,
  Box,
  Tooltip,
  styled,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { typeConfig } from "../constants";
import { formatRelativeTimeLuxon } from "../../../utilities/commonUtilities";

const StyledCardContent = styled(CardContent)({
  display: "flex",
  gap: 16,
  paddingRight: 32, 
  paddingLeft: 16,  
  paddingTop: 16,   
  paddingBottom: 16, 
  "&:last-child": {
    paddingBottom: 16,
  },
  maxWidth: "350px",
  width: "100%",
  flex: 1,
});

const IconBox = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 40,
})

// Right content wrapper
const RightContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  justifyContent: "space-between", // Push footer to bottom
  height: "100%", // Take full height
});

// Message wrapper (title + message)
const MessageContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

// Ellipsis text
const MessageText = styled(Typography)({
  color: "#000",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  maxWidth: "200px",
  whiteSpace: "normal",
});

const FooterBox = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  mt: "auto", // Ensure it stays at bottom
})

export default function NotificationItem({
  notification,
  onMarkAsRead,
}) {
  const [isHovered, setIsHovered] = useState(false);
  const config = typeConfig[notification.notification_type];

  return (
    <Card
      variant="outlined"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        position: "relative",
        borderRadius: 2,
        transition: "all 0.2s",
        bgcolor: config.bg,
        minHeight: 120,
        height: "100%",
        borderColor: config?.border,
        boxShadow: isHovered ? 4 : 1,
        cursor: "pointer",
        display: "flex",
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >

      <StyledCardContent>
        {/* Left icon */}
        <IconBox>
          <IconButton sx={{ fontSize: 24, color: config?.textColor }}>
            {config?.icon}
          </IconButton>
        </IconBox>

        {/* Right content area */}
        <RightContent >
          {/* Message content */}
          <MessageContent>
            <Typography variant="subtitle2" fontWeight="600"color={config.textColor}>
              {notification.title}
            </Typography>

            <Tooltip
              title={notification.message.length > 80 ? notification.message : ""}
              arrow
              placement="bottom"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#2e2828ff",
                    color: "white",
                    fontSize: "0.875rem",
                  },
                }
              }}
            >
              <MessageText variant="body2">
                {notification.message}
              </MessageText>
            </Tooltip>
          </MessageContent>

          {/* Footer section */}
          <FooterBox>
            <Box display="flex" alignItems="center" gap={0.5}>
              <AccessTimeIcon sx={{ fontSize: 14, color: '#000'}} />
              <Typography variant="caption" color="#000" >
                {formatRelativeTimeLuxon(notification.created_on)}
              </Typography>
            </Box>

            <Button
              size="small"
              variant="text"
              sx={{
                fontSize: "0.75rem",
                textTransform: "none",
                color: '#fff',
                bgcolor: config.textColor,
                fontWeight: "bold",
                border: "1px solid #ccc",
                borderRadius: "6px",
                borderColor: `${config?.color}`,
                px: 1.5,
                "&:hover": {
                  bgcolor: `${config?.textColor}`,
                  color: "#fff"
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                onMarkAsRead(notification.id)
              }}
            >
              {"Mark as Read"}
            </Button>
          </FooterBox>
        </RightContent>
      </StyledCardContent>
    </Card>
  );
}